import React, { useState, useEffect } from 'react';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Star, Check, X, Eye } from 'lucide-react';
import { useToast } from '../../hooks/use-toast';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const AdminReviewModeration = () => {
  const { toast } = useToast();
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('pending');
  const [selectedReview, setSelectedReview] = useState(null);
  const [showViewDialog, setShowViewDialog] = useState(false);

  useEffect(() => {
    loadReviews(activeTab);
  }, [activeTab]);

  const loadReviews = async (status) => {
    try {
      setLoading(true);
      const token = localStorage.getItem('accessToken');
      const response = await axios.get(`${API}/reviews/admin/all?status=${status}&limit=100`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setReviews(response.data.reviews || []);
    } catch (error) {
      console.error('Load reviews error:', error);
      toast({
        title: 'Error',
        description: 'Failed to load reviews',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleModerate = async (reviewId, action) => {
    try {
      const token = localStorage.getItem('accessToken');
      await axios.patch(`${API}/reviews/admin/${reviewId}/moderate`, 
        { status: action === 'approve' ? 'approved' : 'rejected' },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast({
        title: 'Success',
        description: `Review ${action === 'approve' ? 'approved' : 'rejected'} successfully`
      });
      
      loadReviews(activeTab);
    } catch (error) {
      console.error('Moderate review error:', error);
      toast({
        title: 'Error',
        description: error.response?.data?.detail || 'Failed to moderate review',
        variant: 'destructive'
      });
    }
  };

  const handleView = (review) => {
    setSelectedReview(review);
    setShowViewDialog(true);
  };

  const renderStars = (rating) => {
    return (
      <div className="flex gap-1">
        {[1, 2, 3, 4, 5].map((star) => (
          <Star
            key={star}
            className={`w-4 h-4 ${star <= rating ? 'fill-yellow-400 text-yellow-400' : 'text-gray-300'}`}
          />
        ))}
      </div>
    );
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusBadge = (status) => {
    const badges = {
      pending: 'bg-yellow-100 text-yellow-700',
      approved: 'bg-green-100 text-green-700',
      rejected: 'bg-red-100 text-red-700'
    };
    
    return (
      <span className={`px-2 py-1 rounded text-xs font-medium ${badges[status]}`}>
        {status.charAt(0).toUpperCase() + status.slice(1)}
      </span>
    );
  };

  return (
    <div className="p-6">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Review Moderation</h1>
        <p className="text-gray-600 mt-1">Approve or reject customer reviews</p>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="pending">Pending</TabsTrigger>
          <TabsTrigger value="approved">Approved</TabsTrigger>
          <TabsTrigger value="rejected">Rejected</TabsTrigger>
        </TabsList>

        <TabsContent value={activeTab} className="mt-6">
          <Card>
            <CardContent className="p-0">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Product</TableHead>
                    <TableHead>Customer</TableHead>
                    <TableHead>Rating</TableHead>
                    <TableHead>Review</TableHead>
                    <TableHead>Date</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {loading ? (
                    <TableRow>
                      <TableCell colSpan={7} className="text-center py-8 text-gray-500">
                        Loading reviews...
                      </TableCell>
                    </TableRow>
                  ) : reviews.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={7} className="text-center py-8 text-gray-500">
                        No {activeTab} reviews
                      </TableCell>
                    </TableRow>
                  ) : (
                    reviews.map((review) => (
                      <TableRow key={review.id}>
                        <TableCell className="font-medium">
                          {review.productName || 'Unknown Product'}
                        </TableCell>
                        <TableCell>
                          <div>
                            <div className="font-medium">{review.userName || 'Anonymous'}</div>
                            <div className="text-sm text-gray-500">{review.userEmail}</div>
                            {review.verifiedPurchase && (
                              <span className="text-xs text-green-600">✓ Verified Purchase</span>
                            )}
                          </div>
                        </TableCell>
                        <TableCell>{renderStars(review.rating)}</TableCell>
                        <TableCell className="max-w-md">
                          <div className="truncate">{review.review || review.comment || review.title}</div>
                        </TableCell>
                        <TableCell className="text-sm">{formatDate(review.createdAt)}</TableCell>
                        <TableCell>{getStatusBadge(review.status)}</TableCell>
                        <TableCell>
                          <div className="flex gap-2">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleView(review)}
                            >
                              <Eye className="w-4 h-4" />
                            </Button>
                            {review.status === 'pending' && (
                              <>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => handleModerate(review.id, 'approve')}
                                  className="text-green-600 hover:text-green-700"
                                >
                                  <Check className="w-4 h-4" />
                                </Button>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => handleModerate(review.id, 'reject')}
                                  className="text-red-600 hover:text-red-700"
                                >
                                  <X className="w-4 h-4" />
                                </Button>
                              </>
                            )}
                          </div>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      <Dialog open={showViewDialog} onOpenChange={setShowViewDialog}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Review Details</DialogTitle>
          </DialogHeader>
          
          {selectedReview && (
            <div className="space-y-4">
              <div>
                <label className="text-sm font-medium text-gray-500">Product</label>
                <p className="text-lg font-semibold">{selectedReview.productName}</p>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium text-gray-500">Customer</label>
                  <p>{selectedReview.userName || 'Anonymous'}</p>
                  <p className="text-sm text-gray-500">{selectedReview.userEmail}</p>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-500">Rating</label>
                  <div className="mt-1">{renderStars(selectedReview.rating)}</div>
                </div>
              </div>

              <div>
                <label className="text-sm font-medium text-gray-500">Review</label>
                <p className="mt-1 text-gray-900 whitespace-pre-wrap">{selectedReview.comment}</p>
              </div>

              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <label className="font-medium text-gray-500">Date</label>
                  <p>{formatDate(selectedReview.createdAt)}</p>
                </div>
                <div>
                  <label className="font-medium text-gray-500">Status</label>
                  <div className="mt-1">{getStatusBadge(selectedReview.status)}</div>
                </div>
              </div>

              {selectedReview.verifiedPurchase && (
                <div className="bg-green-50 border border-green-200 rounded p-3 text-sm text-green-800">
                  ✓ This is a verified purchase
                </div>
              )}

              {selectedReview.status === 'pending' && (
                <div className="flex gap-3 pt-4">
                  <Button
                    onClick={() => {
                      handleModerate(selectedReview.id, 'approve');
                      setShowViewDialog(false);
                    }}
                    className="bg-green-600 hover:bg-green-700"
                  >
                    <Check className="w-4 h-4 mr-2" />
                    Approve Review
                  </Button>
                  <Button
                    onClick={() => {
                      handleModerate(selectedReview.id, 'reject');
                      setShowViewDialog(false);
                    }}
                    variant="destructive"
                  >
                    <X className="w-4 h-4 mr-2" />
                    Reject Review
                  </Button>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminReviewModeration;
